import useFetchCurrentUser from '@components/features/currentUser/hooks/useFetchCurrentUser';
import useSentry, { SentryTags } from '@components/features/sentry/hooks/useSentry';
import { buildVersion } from '@constants/buildVersion';
import { useMatomo } from '@datapunt/matomo-tracker-react';
import { UserCustomDimensions } from '@models/analytics/Analytics';
import { useEffect } from 'react';
import useFetchVersion from './useFetchVersion';

const docbaseUserTypes = {
    1: 'workflow',
    2: 'administratief',
};

const useAnalytics = () => {
    const { pushInstruction } = useMatomo();
    const { data: user } = useFetchCurrentUser();
    const { setSentryUser, setSentryTag } = useSentry();
    const { data: versionData } = useFetchVersion();

    const setMatomoCustomDimension = (customDimensionId: UserCustomDimensions, value: string) => {
        pushInstruction('setCustomDimension', customDimensionId, value);
    };

    const setApiVersions = () => {
        if (!versionData) return;

        setMatomoCustomDimension(UserCustomDimensions.HublApiVersion, versionData?.version);
        setMatomoCustomDimension(UserCustomDimensions.DocbaseVersion, versionData?.docbaseVersion);
        setSentryTag(SentryTags.DocbaseVersion, versionData?.docbaseVersion);
        setSentryTag(SentryTags.HublApiVersion, versionData?.version);
    };

    useEffect(() => {
        setApiVersions();
    }, [versionData]);

    const setReleaseVersion = () => {
        setMatomoCustomDimension(UserCustomDimensions.HublReactVersion, buildVersion);
        setSentryTag(SentryTags.HublReactVersion, buildVersion);
    };

    useEffect(() => {
        setReleaseVersion();
    }, []);

    const resetUserId = () => {
        setSentryUser(null);
        pushInstruction('resetUserId');
    };

    const setUserDimensions = () => {
        if (!user || !user?.userId) {
            resetUserId();
            return;
        }

        // user
        setSentryUser({ username: user.userId });
        pushInstruction('setUserId', user?.userId);

        // dimensions
        setSentryTag(SentryTags.TenantId, user.tenantId.toString());
        setSentryTag(SentryTags.TenantName, user.tenantName);
        setSentryTag(SentryTags.Erp, user.erp);
        setSentryTag(SentryTags.SSOEmailAddress, user.email);
        setMatomoCustomDimension(UserCustomDimensions.TenantId, user.tenantId.toString());
        setMatomoCustomDimension(UserCustomDimensions.TenantName, user.tenantName);
        setMatomoCustomDimension(UserCustomDimensions.Erp, user.erp);
        setMatomoCustomDimension(UserCustomDimensions.UserType, docbaseUserTypes[user.userType as keyof typeof docbaseUserTypes]);
        setMatomoCustomDimension(UserCustomDimensions.SSOEmailAddress, user.email);
    };

    useEffect(() => {
        setUserDimensions();

        return () => {
            resetUserId();
        };
    }, [user]);
};

export default useAnalytics;
